"use client";

import clsx from "clsx";
import {useContext, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {MutationProposalSideConversationContext, ReviewConversationContext, useMutationProposalReview} from "@/contexts/chat/MutationProposalReviewContext";
import GailConversationWorkspace from "./GailConversationWorkspace";

/** The reviewed editor stays on the page, with the builder's conversation UX when the dock is unavailable. */
export default function MutationProposalReviewSlot({children}: {children: React.ReactNode}) {
   const sourceConversationId = useContext(MutationProposalSideConversationContext);
   const {isDockAvailable} = useGailDock();
   const {t} = useTranslation("ask");
   // Until the person opens or closes it, the conversation sits beside the page; on a phone it would
   // cover the editor and the review it hosts, so it waits behind a button there.
   const [session, setSession] = useState<{sourceConversationId: string | null; conversationId: string | null; isOpen: boolean | null}>(
      {sourceConversationId, conversationId: sourceConversationId, isOpen: null});
   // A new review opens its conversation; finishing the same review preserves the person's selection and dismissal.
   if (session.sourceConversationId !== sourceConversationId) {
      setSession({sourceConversationId, conversationId: sourceConversationId, isOpen: null});
   }
   // Always render the workspace: the dock flag resolves after mount, and changing the tree would remount the page.
   const enabled = !!sourceConversationId && !isDockAvailable;
   const reviewing = !!useMutationProposalReview();
   const closed = session.isOpen !== true;
   const openBelowMdOnly = session.isOpen === null;
   const conversation = useMemo(() => enabled ? {
      open: closed ? () => setSession(current => ({...current, isOpen: true})) : null,
      openBelowMdOnly,
      showPage: () => setSession(current => current.isOpen ? {...current, isOpen: null} : current),
   } : null, [enabled, closed, openBelowMdOnly]);
   // The review bar renders inside the conversation and is portalled into the page, so both sides read this.
   return <ReviewConversationContext.Provider value={conversation}>
      <GailConversationWorkspace
         enabled={enabled}
         conversationLabel={t("toolApproval.openConversation")}
         showConversation={enabled && session.isOpen !== false}
         // The conversation renders the review's decision controls into the editor, so closing only hides it.
         keepConversationMounted
         hideConversationBelowMd={session.isOpen === null}
         conversationId={session.conversationId}
         onSelectConversation={conversationId => setSession(current => ({...current, conversationId}))}
         onNewChat={() => setSession(current => ({...current, conversationId: null}))}
         onClose={() => setSession(current => ({...current, isOpen: false}))}
      >
         {children}
         {/* A review bar offers the conversation itself, so nothing floats over its controls. */}
         {conversation?.open && !reviewing && (
            <Button
               type="secondary"
               compact
               className={clsx("absolute right-4 top-4 z-20", openBelowMdOnly && "md:hidden")}
               onClick={conversation.open}
            >
               <Icon icon="comment" className="mr-2" />
               {t("toolApproval.openConversation")}
            </Button>
         )}
      </GailConversationWorkspace>
   </ReviewConversationContext.Provider>;
}
